import React, { useState, useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { fetchOperationsData } from '../../store/slices/operationsSlice';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Modal } from '../../components/ui/modal';
import TransactionReceipt from '../../components/transactions/TransactionReceipt';
import OperationLayout from '../../components/operations/OperationLayout';
import ProviderCard from '../../components/operations/ProviderCard';
import AmountButton from '../../components/operations/AmountButton';
import { PROVIDERS, PREDEFINED_AMOUNTS } from '../../lib/constants';
import { Method } from '../../services/api/operationsService';
import { addTransaction, reverseTransaction } from '../../store/slices/transactionsSlice';
import { operationsService } from '../../services/api/operationsService';
import { printService } from '../../services/printService';
import { Transaction } from '../../types';

const STEPS = [
  { label: 'Compañía' },
  { label: 'Número' },
  { label: 'Monto' },
  { label: 'Confirmar' },
];

const ACCENT = '#2563eb';

const RecargasPageSteps: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [amount, setAmount] = useState<number | ''>('');
  const [selectedProvider, setSelectedProvider] = useState('');
  const [customAmount, setCustomAmount] = useState<string>('');

  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [currentTransaction, setCurrentTransaction] = useState<Transaction | null>(null);

  const dispatch = useAppDispatch();
  const { wallet } = useAppSelector((state) => state.wallet as any);
  const { rechargeMethods } = useAppSelector((state) => state.operations as any);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (!fetchedRef.current && (!rechargeMethods || rechargeMethods.length === 0)) {
      fetchedRef.current = true;
      dispatch(fetchOperationsData());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleProviderSelect = (providerId: string) => {
    setSelectedProvider(providerId);
    setError(null);
    setCurrentStep(2);
  };

  const handlePhoneSubmit = () => {
    if (!phoneNumber || phoneNumber.length < 8) {
      setError('Ingrese un número de teléfono válido');
      return;
    }
    setError(null);
    setCurrentStep(3);
  };

  const handlePhoneInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 10) setPhoneNumber(value);
  };

  const handleAmountSelect = (selectedAmount: number) => {
    setAmount(selectedAmount);
    setCustomAmount('');
    setError(null);
    setCurrentStep(4);
  };

  const handleCustomAmountSubmit = () => {
    if (!customAmount || parseInt(customAmount, 10) <= 0) {
      setError('Ingrese un monto válido');
      return;
    }
    setAmount(parseInt(customAmount, 10));
    setError(null);
    setCurrentStep(4);
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
    setError(null);
  };

  const handleSubmit = async () => {
    if (!phoneNumber || !selectedProvider || amount === '') {
      setError('Complete todos los campos para continuar');
      return;
    }
    const walletBalance = wallet?.balance ?? 0;
    if (walletBalance < Number(amount)) {
      setError('Saldo insuficiente para realizar esta recarga');
      return;
    }
    setIsProcessing(true);
    setError(null);
    try {
      const response = await operationsService.recharge(phoneNumber, Number(amount), selectedProvider);
      const transactionId = response.data?.xid || response.data?.transactionId || `rec-${Date.now()}`;
      const providerObj = rechargeMethods.find((p: Method) => p.id === selectedProvider) ||
                          PROVIDERS.RECARGAS.find((p: Method) => p.id === selectedProvider);
      const providerName = providerObj?.name || selectedProvider;
      const transaction: Transaction = {
        id: transactionId,
        type: 'RECARGA',
        amount: Number(amount),
        provider: providerName,
        providerId: selectedProvider,
        date: new Date().toISOString(),
        status: 'COMPLETED',
        reference: Math.random().toString(36).substring(2, 10).toUpperCase(),
        details: { phoneNumber, amount: Number(amount), provider: providerName, providerId: selectedProvider },
        reversible: true,
      };
      dispatch(addTransaction(transaction));
      setSuccess(`Recarga de RD$${amount} completada con éxito al número ${phoneNumber}`);
      setCurrentTransaction(transaction);
      setShowReceiptModal(true);
      setPhoneNumber('');
      setAmount('');
      setCustomAmount('');
      setSelectedProvider('');
      setCurrentStep(1);
    } catch (err: any) {
      setError(err.message || 'Error al procesar la recarga. Intente nuevamente.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReverseRecharge = async (transactionId: string) => {
    setIsProcessing(true);
    setError(null);
    try {
      const result = await dispatch(reverseTransaction(transactionId)).unwrap();
      if (result && result.success) {
        setSuccess('La recarga ha sido reversada exitosamente.');
        setShowReceiptModal(false);
      } else {
        setError('No se pudo revertir la recarga. Intente nuevamente.');
      }
    } catch (err: any) {
      setError(err.message || 'No se pudo revertir la recarga. Intente nuevamente.');
    } finally {
      setIsProcessing(false);
    }
  };

  const providerList: Method[] =
    rechargeMethods && rechargeMethods.length > 0 ? rechargeMethods : PROVIDERS.RECARGAS;

  const renderStepContent = () => {
    switch (currentStep) {

      case 1:
        return (
          <div className="space-y-5 animate-fadeIn">
            <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Seleccione la compañía</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {providerList.map((p: Method) => (
                <ProviderCard
                  key={p.id} id={p.id} name={p.name} logo={p.logo}
                  onClick={handleProviderSelect} accentColor={ACCENT}
                />
              ))}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6 animate-fadeIn">
            <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Ingrese el número de teléfono</p>
            <div className="max-w-sm mx-auto">
              <Input
                label="Número de teléfono"
                value={phoneNumber}
                onChange={handlePhoneInputChange}
                placeholder="0000000000"
                fullWidth size="touch" type="tel"
                leftAddon="+1"
                useCustomKeyboard keyboardType="numeric"
              />
            </div>
            <div className="flex justify-between pt-2">
              <Button variant="outline" size="lg" onClick={handleBack}>← Atrás</Button>
              <Button size="lg" onClick={handlePhoneSubmit} disabled={!phoneNumber || phoneNumber.length < 8}>
                Continuar →
              </Button>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6 animate-fadeIn">
            <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Seleccione el monto</p>
            <div className="grid grid-cols-3 gap-3">
              {PREDEFINED_AMOUNTS.map((a) => (
                <AmountButton key={a} amount={a} onClick={handleAmountSelect} accentColor={ACCENT} />
              ))}
            </div>
            <div className="border-t border-slate-100 pt-5">
              <p className="text-sm font-semibold text-slate-500 mb-3">O ingrese otro monto:</p>
              <Input
                value={customAmount}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, '');
                  if (v.length <= 5) { setCustomAmount(v); setAmount(v ? parseInt(v, 10) : ''); }
                }}
                placeholder="Monto personalizado"
                fullWidth size="touch" type="tel"
                leftAddon="RD$"
                useCustomKeyboard keyboardType="numeric"
              />
            </div>
            <div className="flex justify-between pt-2">
              <Button variant="outline" size="lg" onClick={handleBack}>← Atrás</Button>
              <Button size="lg" onClick={handleCustomAmountSubmit} disabled={!customAmount}>Continuar →</Button>
            </div>
          </div>
        );

      case 4: {
        const providerObj = rechargeMethods.find((p: Method) => p.id === selectedProvider) ||
                            PROVIDERS.RECARGAS.find((p: Method) => p.id === selectedProvider);
        const providerName = providerObj?.name || selectedProvider;
        return (
          <div className="space-y-6 animate-fadeIn">
            <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Confirmar recarga</p>
            <div className="rounded-2xl border border-slate-100 divide-y divide-slate-100 overflow-hidden"
              style={{ background: '#f8fafc' }}>
              {[
                { label: 'Operador', value: providerName },
                { label: 'Número', value: `+1 ${phoneNumber}` },
              ].map(({ label, value }) => (
                <div key={label} className="flex justify-between items-center px-5 py-4">
                  <span className="text-sm text-slate-500 font-medium">{label}</span>
                  <span className="text-sm font-bold text-slate-800">{value}</span>
                </div>
              ))}
              <div className="flex justify-between items-center px-5 py-4" style={{ background: `${ACCENT}0d` }}>
                <span className="text-sm text-slate-500 font-medium">Monto</span>
                <span className="text-2xl font-black" style={{ color: ACCENT }}>RD$ {amount}</span>
              </div>
            </div>
            <Button size="touch" fullWidth isLoading={isProcessing} onClick={handleSubmit}>
              {isProcessing ? 'Procesando...' : '✓ Confirmar Recarga'}
            </Button>
            <div className="flex justify-start">
              <Button variant="outline" size="lg" onClick={handleBack} disabled={isProcessing}>← Atrás</Button>
            </div>
          </div>
        );
      }

      default: return null;
    }
  };

  return (
    <>
      <OperationLayout
        title="Recargas"
        description="Realice recargas a cualquier operador"
        steps={STEPS}
        currentStep={currentStep}
        error={error}
        success={success}
        accentColor={ACCENT}
        icon={
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"/>
          </svg>
        }
      >
        {renderStepContent()}
      </OperationLayout>

      <Modal isOpen={showReceiptModal} onClose={() => setShowReceiptModal(false)}
        title="Resumen de Transacción" size="md">
        {currentTransaction && (
          <TransactionReceipt
            transaction={currentTransaction}
            onClose={() => setShowReceiptModal(false)}
            onPrint={() => { if (currentTransaction) printService.printTicket(currentTransaction); }}
            onReverse={handleReverseRecharge}
          />
        )}
      </Modal>
    </>
  );
};

export default RecargasPageSteps;
